import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cn } from '@/lib/ui/utils';

export type StatusPulseTone = 'error' | 'warning' | 'success' | 'info' | 'neutral';

const TONE_CLASSES: Readonly<Record<StatusPulseTone, string>> = {
  error: 'bg-feedback-error-accent',
  warning: 'bg-feedback-warning-accent',
  success: 'bg-feedback-success-accent',
  info: 'bg-feedback-info-accent',
  neutral: 'bg-foreground-tertiary',
};

interface StatusPulseProps extends Omit<ComponentPropsWithoutRef<'span'>, 'children'> {
  tone?: StatusPulseTone;
}

export function StatusPulse({ tone = 'error', className, ...props }: StatusPulseProps): ReactNode {
  const toneClass = TONE_CLASSES[tone];

  return (
    <span
      aria-hidden="true"
      data-tone={tone}
      className={cn(
        'relative inline-flex size-2.5 shrink-0 items-center justify-center overflow-visible rounded-full',
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          'absolute inset-0 rounded-full opacity-35 motion-safe:animate-ping motion-reduce:animate-none',
          toneClass,
        )}
      />
      <span className={cn('relative size-1.5 rounded-full', toneClass)} />
    </span>
  );
}
